import React, { Fragment, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import * as S from './styles';

import IconeChippuAvaliacao from '../IconeChippuAvaliacao';

import imagem_default_usuario from '@/assets/DefaultUser.jpg';
import { GeneroTituloCritica, PlataformaTituloCritica } from '@/types/critica';
import { theme } from '@/styles/theme';
import Text from '@/components/atoms/Text';
import Heading from '@/components/atoms/Heading';
import useBreakpoint from '@/hooks/useBreakpoint';

type CriticaVisualizacaoProps = {
  $titulo: string;
  $autor: string;
  $nota: number;
  $duracao?: string;
  $diretor?: string;
  $data?: string;
  $imagemAutor?: string;
  $imagemCartaz?: string;
  $aoClicar?: () => void;
  $generos?: GeneroTituloCritica[];
  $plataformas?: PlataformaTituloCritica[];
};

export const CriticaVisualizacao = ({
  $titulo,
  $autor,
  $nota,
  $duracao,
  $diretor,
  $data,
  $imagemAutor = undefined,
  $imagemCartaz,
  $aoClicar,
  $generos,
  $plataformas,
}: CriticaVisualizacaoProps) => {
  const { widthWindow } = useBreakpoint();
  const plataformasDisponiveis = useMemo(() => {
    return $plataformas?.filter(
      (plataforma) => plataforma?.plataformaStreaming?.paisID === '55',
    );
  }, [$plataformas]);

  return (
    <S.Container onClick={$aoClicar}>
      <S.Conteudo>
        <S.ImagemCartaz>
          <Image
            src={$imagemCartaz || imagem_default_usuario}
            alt="Cartaz do filme"
            fill
            sizes="180px"
            style={{
              backgroundSize: 'cover',
              borderRadius: 2,
            }}
          />
        </S.ImagemCartaz>
        <S.Corpo>
          <Text
            $size={5}
            $color={theme.tokens.colors.news.description}
            $align="left"
            $fontFamily="news"
            $weight="medium"
            $highContrast={false}
            $text={'Nota da Crítica'}
            style={{
              textTransform: 'uppercase',
            }}
          />
          <S.Avaliacao>
            <S.Nota>
              {Array.from({ length: 5 }).map((_, i) => {
                return (
                  <IconeChippuAvaliacao
                    key={`${i}${$titulo}${$nota}chippu`}
                    cor={theme.colors.accent.accent[9]}
                    altura={38}
                    largura={35}
                    offset={$nota - i}
                  />
                );
              })}
            </S.Nota>
            <S.Usuario>
              <S.ImagemPerfil>
                <Image
                  src={$imagemAutor || imagem_default_usuario}
                  alt={'Imagem Perfil Usuario'}
                  fill
                  style={{
                    objectFit: 'cover',
                  }}
                />
              </S.ImagemPerfil>
              <Text
                $size={1}
                $color={theme.tokens.colors.news.author}
                $align="left"
                $fontFamily="news"
                $weight="bold"
                $highContrast={false}
                $text={$autor}
                style={{
                  textTransform: 'uppercase',
                  lineHeight: `${theme.typography.font.line_height[4] * 2}px`,
                }}
              />
            </S.Usuario>
          </S.Avaliacao>

          {plataformasDisponiveis && plataformasDisponiveis?.length > 0 && (
            <S.PlataformasContainer>
              <Text
                $size={2}
                $color={theme.tokens.colors.news.description}
                $align="left"
                $fontFamily="news"
                $weight="medium"
                $highContrast={false}
                $text={'Onde assistir'}
                style={{
                  textTransform: 'uppercase',
                }}
              />
              <div style={{ marginTop: 15 }}>
                {plataformasDisponiveis.map((plataforma, i) => (
                  <Link passHref href={plataforma.link || '/'} key={i}>
                    <Image
                      src={
                        plataforma.plataformaStreaming?.logoArquivo?.urlx1 ||
                        '/images/DefaultUser.jpg'
                      }
                      width={30}
                      height={30}
                      style={{ borderRadius: 5 }}
                      alt="Plataforma"
                    />
                  </Link>
                ))}
              </div>
            </S.PlataformasContainer>
          )}
        </S.Corpo>
      </S.Conteudo>
      <S.Rodape>
        <Heading
          $priority={3}
          $size={7}
          $align="left"
          $color={theme.tokens.colors.black_contrast}
          $highContrast={false}
          $weight="bold"
          $text={$titulo}
          $fontFamily="news"
          style={{
            ...(widthWindow > 1200
              ? {
                  fontSize: `${
                    theme.typography.font.size[4] +
                    theme.typography.font.size[5]
                  }px`,
                  lineHeight: `${
                    theme.typography.font.size[8] +
                    theme.typography.font.size[4]
                  }px`,
                }
              : {
                  lineHeight: `${
                    theme.typography.font.size[7] +
                    theme.typography.font.size[1]
                  }px`,
                }),
          }}
        />
        <Text
          $size={3}
          $color={theme.tokens.colors.news.description}
          $align="left"
          $fontFamily="news"
          $weight="medium"
          $highContrast={false}
          $text={
            <S.GenerosContainer>
              {$generos?.map(($genero, i) => (
                <Fragment key={i}>
                  <div>{$genero.genero.nome}</div>
                  {i < $generos.length - 1 ? <div>{'|'}</div> : ''}
                </Fragment>
              ))}
            </S.GenerosContainer>
          }
        />
        {$diretor ? (
          <Text
            $size={3}
            $color={theme.tokens.colors.news.description}
            $align="left"
            $fontFamily="news"
            $weight="medium"
            $highContrast={false}
            $text={$diretor}
          />
        ) : null}
        <Text
          $size={3}
          $color={theme.tokens.colors.news.description}
          $align="left"
          $fontFamily="news"
          $weight="medium"
          $highContrast={false}
          $text={`${$duracao} ${$data ? `| ${$data}` : ''}`}
        />
      </S.Rodape>
    </S.Container>
  );
};
